import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  LayoutDashboard, Sparkles, Monitor, Clock, Brain, Wand2,
  GitCompare, Timer, FileText, BookOpen, Activity, Target, Bell, ChevronRight,
  Lightbulb, Loader2, ArrowUpRight, Layers, Compass
} from 'lucide-react';
import { StatsCard } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { PageLayout } from '@/components/layout/PageLayout';
import { useGuideTour } from '@/context/GuideTourContext';
import { api } from '@/services/client';
import { toast } from 'sonner';

interface DashboardStats {
  activeTabs: number;
  sessions: number;
  appsGenerated: number;
  focusSessions: number;
}

interface Suggestion {
  id?: string;
  title: string;
  desc: string;
  reason?: string;
  confidence: number;
  action: string;
  to: string;
}

interface ActivityItem {
  text: string;
  time: string;
  icon: React.ComponentType<{ className?: string }>;
  /** Where the row navigates on click. Empty string means non-clickable. */
  to: string;
}

const RECENT_ACTIVITY_LIMIT = 4;

function cleanActivityText(text: string): string {
  const trimmed = text.trim();
  return trimmed.replace(/^(created|updated) tab:\s*/i, '').replace(/^generated app:\s*/i, '');
}

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days > 1 ? 's' : ''} ago`;
}

function activityIconFor(action: string): React.ComponentType<{ className?: string }> {
  const lower = action.toLowerCase();
  if (lower.includes('generated app') || lower.includes('app')) return Sparkles;
  if (lower.includes('updated tab') || lower.includes('created tab') || lower.includes('tab')) return Layers;
  if (lower.includes('focus')) return Target;
  if (lower.includes('workspace') || lower.includes('session')) return Monitor;
  return Brain;
}

// Routes an activity row to its destination. Tab → workspace monitor, app →
// the app's details page, focus → productivity pomodoro, session → the
// session overview. Anything else falls back to the activity feed so the
// row is never a dead click.
function activityRouteFor(action: string): string {
  const lower = action.toLowerCase();
  if (lower.includes('updated tab') || lower.includes('created tab') || lower.includes('tab ')) {
    return '/monitor';
  }
  if (lower.includes('generated app') || lower.includes('app ')) {
    return '/home?section=suggestions';
  }
  if (lower.includes('focus')) {
    return '/productivity?section=pomodoro';
  }
  if (lower.includes('workspace') || lower.includes('session')) {
    return '/home?section=suggestions';
  }
  return '/home?section=activity';
}

const insightCardClass =
  'group relative flex h-full flex-col rounded-2xl border border-khiimori-border/50 bg-khiimori-bg-elevated/40 p-5 transition-all hover:border-primary-500/40 hover:bg-khiimori-bg-elevated/80 hover:shadow-lg hover:shadow-primary-500/5';

function suggestionDestination(raw: Record<string, unknown>): string {
  const type = String(raw.type ?? '');
  const title = String(raw.title ?? '').toLowerCase();

  if (type === 'focus_reminder' || type === 'break') return '/productivity?section=pomodoro';
  if (title.includes('compare') || title.includes('comparison')) return '/compare';
  if (title.includes('synth')) return '/synthesize';
  if (
    title.includes('gentab') ||
    title.includes('dashboard') ||
    title.includes('generate') ||
    type === 'workflow'
  ) {
    return '/generate';
  }
  if (type === 'tab_close' || type === 'tab_group') return '/tabs';
  return '/home?section=suggestions';
}

function mapSmartSuggestion(raw: Record<string, unknown>): Suggestion {
  const title = String(raw.title ?? 'Suggestion');
  return {
    id: raw.id ? String(raw.id) : undefined,
    title,
    desc: String(raw.description ?? ''),
    reason: String(raw.reason ?? ''),
    confidence: Math.round(Number(raw.confidence ?? 80)),
    action: String(raw.actionLabel ?? raw.action_label ?? 'View'),
    to: suggestionDestination(raw),
  };
}

function extractTabTopics(activities: Record<string, unknown>[]): string[] {
  const topics = new Set<string>();
  for (const activity of activities) {
    const text = String(activity.details ?? activity.description ?? activity.text ?? '').toLowerCase();
    if (!text.includes('tab:')) continue;
    const label = text.split('tab:').pop()?.trim() ?? '';
    const words = label.split(/\s+/).filter((w) => w.length > 3);
    if (words.length > 0) topics.add(words.slice(0, 3).join(' '));
    if (topics.size >= 2) break;
  }
  return [...topics];
}

function buildActivitySuggestions(
  activities: Record<string, unknown>[],
  tabCount: number,
): Suggestion[] {
  const suggestions: Suggestion[] = [];
  const tabActivities = activities.filter((a) => {
    const text = String(a.details ?? a.description ?? '').toLowerCase();
    return text.includes('tab:') || text.includes('tab ');
  });
  const topics = extractTabTopics(activities);
  const topicHint = topics.length > 0 ? ` (${topics.join(', ')})` : '';

  if (tabCount >= 3) {
    suggestions.push({
      title: 'Create GenTab Dashboard',
      desc: `You have ${tabCount} active tabs${topicHint}. Turn them into one interactive app.`,
      reason: 'Based on your current tab activity',
      confidence: Math.min(92, 68 + tabCount * 3),
      action: 'Generate App',
      to: '/generate',
    });
  }

  if (tabActivities.length >= 2) {
    suggestions.push({
      title: 'Synthesize Recent Research',
      desc: `${tabActivities.length} tabs updated recently — compile a summary from your reading.`,
      reason: 'Derived from your recent tab updates',
      confidence: 85,
      action: 'Synthesize',
      to: '/synthesize',
    });
    suggestions.push({
      title: 'Generate Research Paper',
      desc: `${tabActivities.length} research tabs ready — turn them into a journal-style manuscript.`,
      reason: 'Derived from your recent tab updates',
      confidence: 82,
      action: 'Write Paper',
      to: '/apps/synthesize',
    });
  }

  if (tabCount >= 5) {
    suggestions.push({
      title: 'Organize Open Tabs',
      desc: `${tabCount} tabs open. Group by domain to cut clutter and context switching.`,
      reason: 'High tab count detected in your workspace',
      confidence: 80,
      action: 'Organize Tabs',
      to: '/tabs',
    });
  }

  if (suggestions.length === 0 && tabCount > 0) {
    suggestions.push({
      title: 'Explore Smart Actions',
      desc: 'Open the suggestions hub for AI recommendations tailored to your tabs.',
      reason: 'Get started with contextual help',
      confidence: 75,
      action: 'View Suggestions',
      to: '/home?section=suggestions',
    });
  }

  return suggestions.slice(0, 3);
}

function countFocusSessionsThisWeek(
  pomodoroSessions: Array<{ start_time?: string; type?: string; is_completed?: boolean }>,
  focusSessions: Array<{ start_time?: string; end_time?: string | null; is_active?: boolean }>,
): number {
  const weekStart = Date.now() - 7 * 24 * 60 * 60 * 1000;

  const pomodoroCount = pomodoroSessions.filter((s) => {
    if (!s.is_completed) return false;
    if (s.type && s.type !== 'work') return false;
    const start = s.start_time ? new Date(s.start_time).getTime() : 0;
    return start >= weekStart;
  }).length;

  const focusModeCount = focusSessions.filter((s) => {
    if (s.is_active) return false;
    if (!s.end_time) return false;
    const start = s.start_time ? new Date(s.start_time).getTime() : 0;
    return start >= weekStart;
  }).length;

  return pomodoroCount + focusModeCount;
}

export function DashboardPage() {
  const { startTour } = useGuideTour();
  const [stats, setStats] = useState<DashboardStats>({
    activeTabs: 0,
    sessions: 0,
    appsGenerated: 0,
    focusSessions: 0,
  });
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [recentActivity, setRecentActivity] = useState<ActivityItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);

  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      // Single authoritative stats endpoint replaces the previous N+1 per-session
      // fan-out (which inflated /apps/count with stale data and returned 0 from
      // /productivity/stats). Pomodoro + focus-mode stay as separate calls so
      // we can derive this week's focus-session count via
      // countFocusSessionsThisWeek without involving the Handler in
      // uuid→uint resolution.
      const [statsRes, activityRes, suggestionsRes, pomodoroRes, focusModeRes] =
        await Promise.all([
          api
            .get('/sessions/stats')
            .catch(() => ({ data: { active_tabs: 0, sessions: 0, apps_generated: 0 } })),
          api
            .get('/activity', { params: { time_range: 'week' } })
            .catch(() => ({ data: { activities: [] as unknown[] } })),
          api
            .get('/smart-actions/suggestions')
            .catch(() => ({ data: { suggestions: [] as unknown[] } })),
          api
            .get('/pomodoro/history')
            .catch(() => ({ data: { sessions: [] as unknown[] } })),
          api
            .get('/focus-mode/sessions')
            .catch(() => ({ data: { sessions: [] as unknown[] } })),
        ]);

      const statsData = statsRes.data ?? {};
      const totalTabs = Number(statsData.active_tabs ?? 0);
      const totalSessions = Number(statsData.sessions ?? 0);
      const totalApps = Number(statsData.apps_generated ?? 0);

      const pomodoroSessions = Array.isArray(pomodoroRes.data?.sessions)
        ? pomodoroRes.data.sessions
        : [];
      const focusModeSessions = Array.isArray(focusModeRes.data?.sessions)
        ? focusModeRes.data.sessions
        : [];
      const activities = Array.isArray(activityRes.data)
        ? activityRes.data
        : (activityRes.data?.activities ?? []);
      const suggestionsData = suggestionsRes.data?.suggestions ?? [];

      setStats({
        activeTabs: totalTabs,
        sessions: totalSessions,
        appsGenerated: totalApps,
        focusSessions: countFocusSessionsThisWeek(pomodoroSessions, focusModeSessions),
      });

      const mappedSuggestions = suggestionsData
        .slice(0, 3)
        .map((s: Record<string, unknown>) => mapSmartSuggestion(s));

      setSuggestions(
        mappedSuggestions.length > 0
          ? mappedSuggestions
          : buildActivitySuggestions(activities, totalTabs),
      );

      setRecentActivity(
        activities.slice(0, RECENT_ACTIVITY_LIMIT).map((a: Record<string, unknown>) => {
          const raw = String(a.details ?? a.description ?? a.text ?? a.action ?? 'Activity');
          return {
            text: cleanActivityText(raw),
            time: a.timestamp
              ? relativeTime(String(a.timestamp))
              : a.created_at
                ? relativeTime(String(a.created_at))
                : String(a.time ?? ''),
            icon: activityIconFor(raw),
            to: activityRouteFor(raw),
          };
        }),
      );
    } catch (err) {
      console.warn('Dashboard fetch failed', err);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
      setLastRefresh(new Date());
    }
  }, []);

  useEffect(() => {
    fetchDashboardData();
    const interval = setInterval(fetchDashboardData, 60000);
    return () => clearInterval(interval);
  }, [fetchDashboardData]);

  const quickActions = [
    { icon: GitCompare, label: 'Compare', desc: 'Compare products or content', to: '/compare' },
    { icon: Clock, label: 'Timeline', desc: 'Create visual timelines', to: '/timeline' },
    { icon: FileText, label: 'Synthesize', desc: 'Research synthesis', to: '/synthesize' },
    { icon: BookOpen, label: 'Research Paper', desc: 'Journal manuscript', to: '/apps/synthesize' },
    { icon: Wand2, label: 'Generate', desc: 'Create a new app', to: '/generate' },
  ];

  return (
    <PageLayout title="Dashboard" description="Overview of your productivity and tab ecosystem" icon={LayoutDashboard}>
      {loading && (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-primary-400" />
        </div>
      )}
      {!loading && (
        <div className="mx-auto max-w-7xl space-y-8">
          {/* Hero */}
          <div className="relative overflow-hidden rounded-3xl border border-primary-500/20 bg-gradient-to-br from-primary-500/10 via-khiimori-bg-elevated/60 to-khiimori-accent-fuchsia/5 p-8 lg:p-10">
            <div className="absolute -right-20 -top-20 h-64 w-64 rounded-full bg-primary-500/15 blur-3xl" />
            <div className="absolute -bottom-10 right-40 h-48 w-48 rounded-full bg-khiimori-accent-fuchsia/10 blur-3xl" />
            <div className="relative flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-primary-500 to-khiimori-accent-fuchsia shadow-lg shadow-primary-500/25">
                    <Sparkles className="h-5 w-5 text-khiimori-text-primary" />
                  </div>
                  <span className="rounded-full bg-primary-500/15 px-3 py-1 text-xs font-medium text-primary-400">
                    AI-Powered
                  </span>
                </div>
                <h2 className="text-xl font-semibold text-khiimori-text-primary lg:text-2xl">
                  Your intelligent tab management and app generation hub
                </h2>
                <p className="max-w-lg text-sm text-khiimori-text-primary/50 lg:text-base">
                  {stats.activeTabs > 0
                    ? `Tracking ${stats.activeTabs} active tab${stats.activeTabs === 1 ? '' : 's'} across ${stats.sessions} session${stats.sessions === 1 ? '' : 's'}.`
                    : 'Track, organize, and create — all powered by AI.'}
                </p>
              </div>
              <div className="flex flex-wrap gap-3">
                <Button
                  variant="primary"
                  onClick={() => startTour(0)}
                  leftIcon={<Compass className="h-4 w-4 text-primary-300" />}
                >
                  Start Guide Tour
                </Button>
                <Link to="/monitor">
                  <Button variant="gradient" leftIcon={<Monitor className="h-4 w-4" />}>
                    Open Tab Monitor
                  </Button>
                </Link>
                <Link to="/generate">
                  <Button variant="secondary" leftIcon={<Wand2 className="h-4 w-4" />}>
                    Generate New App
                  </Button>
                </Link>
              </div>
            </div>
          </div>

          {/* Stats row */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-[11px] uppercase tracking-wide text-khiimori-text-primary/40">
                Workspace stats
              </p>
              {lastRefresh && (
                <p
                  className="flex items-center gap-1.5 text-[11px] text-khiimori-text-primary/40"
                  title={`Refreshed at ${lastRefresh.toLocaleString()}`}
                >
                  <span className="relative flex h-1.5 w-1.5">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-success-400/60" />
                    <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-success-500" />
                  </span>
                  Updated {lastRefresh.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                </p>
              )}
            </div>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <StatsCard icon={Monitor} label="Active Tabs" value={stats.activeTabs} color="violet" />
              <StatsCard icon={Clock} label="Sessions" value={stats.sessions} color="cyan" />
              <StatsCard
                icon={Wand2}
                label="Apps Generated"
                value={stats.appsGenerated}
                trend="all time"
                trendDirection="neutral"
                color="emerald"
              />
              <StatsCard
                icon={Target}
                label="Focus Sessions"
                value={stats.focusSessions}
                trend="this week"
                trendDirection="neutral"
                color="amber"
              />
            </div>
          </div>

          {/* Empty-state CTA — only shows when the user has nothing to summarize yet */}
          {stats.sessions === 0 && stats.activeTabs === 0 && recentActivity.length === 0 && (
            <div className="rounded-2xl border border-dashed border-khiimori-border/60 bg-khiimori-bg-elevated/20 p-6 lg:p-8">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="space-y-2">
                  <h3 className="text-base font-semibold text-khiimori-text-primary">
                    Nothing tracked yet
                  </h3>
                  <p className="max-w-xl text-sm text-khiimori-text-primary/60">
                    Install the Khiimori Chrome extension to start syncing tabs, or open the Tab
                    Monitor and create your first workspace session to see live stats here.
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Link to="/monitor">
                    <Button variant="gradient" leftIcon={<Monitor className="h-4 w-4" />}>
                      Open Tab Monitor
                    </Button>
                  </Link>
                  <Link to="/generate">
                    <Button variant="secondary" leftIcon={<Wand2 className="h-4 w-4" />}>
                      Try Generate
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          )}

          {/* Main content rows */}
          <div className="grid gap-6 lg:grid-cols-2 lg:items-stretch">
            {/* Suggestions */}
            <div className="flex min-w-0 flex-col space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="flex items-center gap-2 text-sm font-semibold text-khiimori-text-primary">
                    <Lightbulb className="h-4 w-4 text-amber-400" />
                    Smart Suggestions
                  </h2>
                  <p className="mt-0.5 text-[11px] text-khiimori-text-primary/40">
                    Based on your open tabs and recent activity
                  </p>
                </div>
                <Link
                  to="/home?section=suggestions"
                  className="flex items-center gap-1 text-xs text-primary-400 hover:text-primary-300"
                >
                  View all <ChevronRight className="h-3 w-3" />
                </Link>
              </div>
              <div className="grid gap-3 sm:grid-cols-1 md:grid-cols-3">
                {suggestions.length === 0 ? (
                  <div className="col-span-full rounded-2xl border border-dashed border-khiimori-border/60 bg-khiimori-bg-elevated/30 px-6 py-10 text-center">
                    <Bell className="mx-auto mb-2 h-6 w-6 text-khiimori-text-primary/30" />
                    <p className="text-sm text-khiimori-text-primary/60">No suggestions yet</p>
                    <p className="mt-1 text-xs text-khiimori-text-primary/40">
                      Open some tabs or check back after browsing
                    </p>
                  </div>
                ) : (
                  suggestions.map((s, i) => (
                    <Link key={s.id ?? i} to={s.to} className={insightCardClass}>
                      <div className="mb-3 flex items-center justify-between">
                        <span className="rounded-full bg-primary-500/15 px-2 py-0.5 text-[10px] font-medium text-primary-400">
                          {s.confidence}% confidence
                        </span>
                        <Sparkles className="h-3.5 w-3.5 text-amber-400/70 transition-transform group-hover:scale-110" />
                      </div>
                      <h3 className="mb-1 text-sm font-semibold text-khiimori-text-primary">{s.title}</h3>
                      <p className="mb-2 text-xs leading-relaxed text-khiimori-text-primary/55">{s.desc}</p>
                      {s.reason && (
                        <p className="mb-3 text-[10px] italic text-khiimori-text-primary/35">{s.reason}</p>
                      )}
                      <span className="inline-flex items-center gap-1 text-xs font-medium text-primary-400 transition-colors group-hover:text-primary-300">
                        {s.action} <ArrowUpRight className="h-3 w-3" />
                      </span>
                    </Link>
                  ))
                )}
              </div>
            </div>

            {/* Recent Activity — wider column, stretches to match suggestions height */}
            <div className="flex h-full min-w-0 flex-col space-y-3">
              <div className="flex items-center justify-between">
                <h2 className="text-sm font-semibold text-khiimori-text-primary">Recent Activity</h2>
                <Link
                  to="/home?section=activity"
                  className="text-[11px] text-primary-400 hover:text-primary-300"
                >
                  See all
                </Link>
              </div>
              <div className="flex w-full flex-1 flex-col rounded-2xl border border-khiimori-border/50 bg-khiimori-bg-elevated/40 p-3">
                {recentActivity.length === 0 ? (
                  <p className="flex flex-1 items-center justify-center px-2 py-3 text-center text-xs text-khiimori-text-primary/45">
                    No recent activity
                  </p>
                ) : (
                  <div className="flex flex-1 flex-col divide-y divide-khiimori-border/30">
                    {recentActivity.map((a, i) => {
                      const Icon = a.icon;
                      const RowContent = (
                        <>
                          <div className="mt-0.5 shrink-0 rounded-md bg-khiimori-bg-elevated/80 p-1">
                            <Icon className="h-3 w-3 text-khiimori-text-primary/50" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="break-words text-xs leading-snug text-khiimori-text-primary/80">
                              {a.text}
                            </p>
                            <p className="mt-0.5 text-[10px] text-khiimori-text-primary/35">{a.time}</p>
                          </div>
                          {a.to && (
                            <ArrowUpRight className="mt-1 h-3 w-3 shrink-0 text-khiimori-text-primary/30 transition-colors group-hover/row:text-primary-400" />
                          )}
                        </>
                      );
                      const baseCls =
                        'group/row flex items-start gap-2.5 px-2 py-2.5 transition-colors hover:bg-khiimori-bg-elevated/50';
                      return a.to ? (
                        <Link key={i} to={a.to} className={baseCls}>
                          {RowContent}
                        </Link>
                      ) : (
                        <div key={i} className={baseCls}>
                          {RowContent}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Quick actions — full-width spread row */}
          <div className="space-y-4">
            <h2 className="text-sm font-semibold text-khiimori-text-primary">Quick Actions</h2>
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
              {quickActions.map((action, i) => {
                const Icon = action.icon;
                return (
                  <Link key={i} to={action.to} className="h-full">
                    <div className="group flex h-full min-h-[5.5rem] items-center gap-3 rounded-2xl border border-khiimori-border/50 bg-khiimori-bg-elevated/40 p-4 transition-all hover:border-primary-500/40 hover:bg-khiimori-bg-elevated/80 sm:gap-4 sm:p-5">
                      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-primary-500/20 to-khiimori-accent-fuchsia/20 transition-colors group-hover:from-primary-500/30 group-hover:to-khiimori-accent-fuchsia/30 sm:h-12 sm:w-12">
                        <Icon className="h-5 w-5 text-primary-300" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-semibold text-khiimori-text-primary">{action.label}</div>
                        <div className="mt-0.5 line-clamp-2 text-xs leading-snug text-khiimori-text-primary/45">
                          {action.desc}
                        </div>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </PageLayout>
  );
}
